import React, { useState, useReducer, useContext } from 'react';
import Downshift from 'downshift';
import PropTypes from 'prop-types';
import qs from 'qs';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import getResponseJson from '../../services/getresponsejson';
import CodeChip from '../codechip/components/codechip';
import { RouteFetcherContext } from '@mk-redbox/route-fetcher';

const styles = theme => ({
  root: {
    position:'relative'
  },
  primaryContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    border: '1px solid black',
    padding:'.8em'
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    '& label': {
      marginRight:'8px'
    }
  },
  selectedItemContainer: {
    display: 'inline-block',
    marginLeft: '1em'
  },
  optionsContainer: {
    position: 'absolute',
    marginLeft: '62px',
    width: '11em',
    height: '170px',
    overflowY: 'auto',
    border: '1px solid black',
    backgroundColor:'white',
    marginTop:'-19px',
    cursor:'pointer',
    padding:'4px',
    zIndex:'999',
    '& div': {
      borderBottom:'1px solid black'
    }
  }
});

// for server api call param
const API_PARAM_CODE_CATEGORY_CATEGORY = {
  HCC: 1,
  ACA: 2
};

// for server api call param
const API_PARAM_CODE_CATEGORY_TYPE = {
  RX: 1,
  MA: 2,
  EP: 3,
  ACA: 4
};

const getResults = (
  code,
  category,
  type,
  backendRoutes
) => {
  const { CodeCategoryAutocomplete } = backendRoutes;

  const params = { code, category, type };

  const queryString = qs.stringify(params);

  const url = `${CodeCategoryAutocomplete}?${queryString}`;

  return fetch(url, {
    credentials: 'include'
  })
  .then(getResponseJson)
  .catch(e => {
    console.error(e.toString());
  });
};

const ACTION_TYPES = {
  CHANGE_INPUT_VALUE: 'CHANGE_INPUT_VALUE'
};

const ACTIONS = {
  changeInputValue: inputValue => ({
    type: ACTION_TYPES.CHANGE_INPUT_VALUE,
    payload: {
      inputValue
    }
  })
};

const autocompleteInitialState = {
  inputValue: ''
};

const autocompleteReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.CHANGE_INPUT_VALUE: {
      const { payload: { inputValue } } = action;
      return {
        ...state,
        inputValue
      };
    }

    default: {
      throw Error('autocompleteReducer should never hit default case');
    }
  }
};

const HCCAutocomplete = ({
  dateOfService,
  selectedEncoding,
  setSelectedEncoding,
  classes,
  handleDelete = () => {},
  codeCategoryLabel = 'CC',
  isACA = false
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fooState, fooDispatch] = useReducer(
    autocompleteReducer,
    autocompleteInitialState
  );
  const backendRoutes = useContext(RouteFetcherContext);

  const handleInputChange = e => {
    const value = e.target.value;

    if (!value || value.length < 1) {
      setOptions([]);
      return;
    }

    setIsLoading(true);

    // use aca if its aca else hcc
    const codeCategoryCategoryParam = isACA
      ? API_PARAM_CODE_CATEGORY_CATEGORY.ACA
      : API_PARAM_CODE_CATEGORY_CATEGORY.HCC;

    // use aca if its aca else ma
    const codeCategoryTypeParam = isACA
      ? API_PARAM_CODE_CATEGORY_TYPE.ACA
      : API_PARAM_CODE_CATEGORY_TYPE.MA

    getResults(
      value,
      codeCategoryCategoryParam,
      codeCategoryTypeParam,
      backendRoutes
    )
    .then(responseJson => {
      const { result } = responseJson;
      setOptions(result);
      setIsLoading(false);
    })
    .catch(e => {
      console.error(e.toString());
      setIsLoading(false);
    });
  };

  const betterOnInputValueChange = v => {
    const nextVal = (v === undefined || v === null) ? '' : v;
    fooDispatch(ACTIONS.changeInputValue(nextVal));
  };

  return (
    <Downshift
      inputValue={fooState.inputValue}
      onInputValueChange={betterOnInputValueChange}
      selectedItem={selectedEncoding}
      onChange={setSelectedEncoding}
      itemToString={item => (item ? item.code : '')}
      onOuterClick={() => fooDispatch(ACTIONS.changeInputValue(''))}
    >
      {({
        getInputProps,
        getLabelProps,
        isOpen,
        getItemProps,
        highlightedIndex,
        selectedItem
      }) => {
        return (
          <div className={classes.root}>
            <div className={classes.primaryContainer}>

              {/* input section */}
              <div className={classes.inputContainer}>
                <label {...getLabelProps()}>{codeCategoryLabel}: </label>
                <input
                  {...getInputProps({
                    placeholder: 'Search Encodings',
                    onChange: handleInputChange
                  })}
                />
                {
                  isLoading
                    ? (<CircularProgress variant="indeterminate" size="20px" />)
                    : null
                }
              </div>

              {/* selected item section */}
              <div className={classes.selectedItemContainer}>
                <span>
                  {`Audited ${codeCategoryLabel}s`}: {
                    selectedEncoding.length > 0 ?
                      selectedEncoding.map(x => (
                        <CodeChip
                          key={x.codeCategoryId}
                          isDelible={true}
                          onDelete={() => handleDelete(x.codeCategoryId, x)}
                          encoding={x}
                        />)) :
                      'NONE'
                  }
                </span>
              </div>

            </div>

            {/* items list section */}
            {
              isOpen && options.length === 1 && options[0].code === null ? (
                <div className={classes.optionsContainer}>
                  <div
                    {...getItemProps({
                        key: null,
                        item: null,
                        index: 0,
                        style: {
                        backgroundColor: 'lightgray',
                        fontWeight: 'bold'
                      }
                    })}
                  >
                    NO MATCHES FOUND
                  </div>
                </div>
              ) : isOpen && options.length > 0 ? (
                <div className={classes.optionsContainer}>
                  {
                    options.map((o, i) => (
                      <div
                        {...getItemProps({
                          key: o.codeCategoryId,
                          item: o,
                          index: i,
                          style: {
                            backgroundColor: highlightedIndex === i ?
                              'lightgray' :
                              'white',
                            fontWeight: selectedItem &&
                              selectedItem.codeCategoryId === o.codeCategoryId ?
                                'bold' :
                                'normal'
                          }
                        })}
                      >
                        {o.code}
                      </div>
                    ))
                  }
                </div>
              ) : null
            }
          </div>
        );
      }}
    </Downshift>
  );
};

HCCAutocomplete.propTypes = {
  handleDelete: PropTypes.func.isRequired,
  codeCategoryLabel: PropTypes.string,
  isACA: PropTypes.bool
};

export default withStyles(styles, { withTheme: true })(HCCAutocomplete);
